import { Col, Row } from 'react-bootstrap';
import React, { SetStateAction, useEffect } from 'react';
import { IconManagement, TrashIcon } from '../../../../../../shared/Icons';
import { ICompanyUser } from '../../../../../shared/user/dto/ICompanyUser';
import { User } from '../../../../../../model/user/User';
import { IAssignUserRequest } from '../../../../dashboard/dto/IAssignUserRequest';
import { CustomImageHandler } from '../../../../../../shared/components/CustomImageHandler';
import { CustomAutocomplete } from '../../../../../../shared/components/Autocomplete';
import { useTranslation } from 'react-i18next';
import CardLayout from '../../../../../../shared/components/card/CardLayout';
import { MUTED_TEXT_COLOR } from '../../../../../../utils/constants';
import { IUserManagement } from '../dto/ICampaignManagement';
import { getTruncatedData } from '../../../../../../utils/helpers/getTruncatedData';
import TooltipOverlay from '../../../../../../shared/components/tooltip/Tooltip';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../store/rootReducer';
import { formatGroupOptions } from '../../../../../../shared/functions/Functions';

interface IManagingProps {
    companyUsers?: ICompanyUser[];
    assignUsers?: (User | IUserManagement)[];
    assignUser?: IAssignUserRequest;
    selectedUser?: ICompanyUser;
    setSelectedUser?: React.Dispatch<SetStateAction<ICompanyUser>>;
    setAssignUser: (a: any) => void;
    setIsDeleteModalOpened: React.Dispatch<SetStateAction<boolean>>;
    isClientTeam?: boolean;
}

function CampaignManagement({
    setIsDeleteModalOpened,
    companyUsers,
    assignUsers,
    selectedUser,
    setSelectedUser,
    assignUser,
    setAssignUser,
    isClientTeam,
}: IManagingProps) {
    const { t } = useTranslation();
    const locale = useSelector((state: IAllStates) => state.auth.user.locale);
    const isDe = locale === 'de';

    useEffect(() => {
        if (assignUser && companyUsers && setSelectedUser) {
            setSelectedUser(companyUsers?.find(user => +user?.id! === Number(assignUser.userId)) as ICompanyUser);
        }
    }, [JSON.stringify(assignUser), JSON.stringify(companyUsers), setSelectedUser]);
    const changeHandler = (paramKey: string, paramValue: number | string | string[]) => {
        setAssignUser({ ...assignUser, [paramKey]: +paramValue });
    };

    const clearedData = companyUsers?.filter(
        coUser => assignUsers?.findIndex(asUser => Number(asUser.id) === Number(coUser.id)) === -1
    );

    return (
        <>
            <div className="border-bottom border-light">
                <Row>
                    <Col md={12} className="pb-4 my-lg-0">
                        <CustomAutocomplete
                            dataArray={clearedData as ICompanyUser[]}
                            isWithImage
                            isManagingModal={true}
                            inputLabel="clientNameAndOptions"
                            photoLabel="profilePhoto.path"
                            selectedStateImage={selectedUser?.profilePhoto?.path as string}
                            queryFilterKey="userId"
                            disableClearable={false}
                            key={assignUsers?.length}
                            inputLabelTranslation={t('general.addUser')}
                            handleChangeParams={changeHandler}
                            me={true}
                            classes="px-3"
                        />
                    </Col>
                </Row>
                <h6 className="text-muted mb-2 px-3">
                    <IconManagement width="20" height="20" color={MUTED_TEXT_COLOR} classes={' mr-2'} />
                    {t('brand.campaign.dashboard.modal.managers', { managersCount: assignUsers?.length })}
                </h6>
                <div className="overflow-auto px-3" style={{ maxHeight: '300px' }}>
                    {assignUsers?.map(user => {
                        const brandName = (user as IUserManagement)?.brandCompany?.displayName;
                        const { truncatedName, isTruncated } = getTruncatedData(brandName ?? '', 35);
                        const hasGroups = !!user?.groups && user?.groups?.length > 0;
                        const formattedOptions = formatGroupOptions(user?.groups, isDe);
                        const truncatedOptions = formatGroupOptions(user?.groups, isDe, 40, true);
                        const isGroupTruncated = formattedOptions.length > 40;

                        return (
                            <CardLayout key={user.id} classCard="mb-3" classBody="p-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <CustomImageHandler
                                            altTag="User Profile"
                                            classes="campaign-card-img mr-2"
                                            thumbnailPath={user.profilePhoto?.thumbnail}
                                            photoPath={user.profilePhoto?.path as string}
                                            name={user.firstName}
                                        />
                                        <div className="d-flex flex-column ">
                                            <p className="mb-0 font-14 font-weight-bold">
                                                {user.firstName} {user.lastName}
                                                {(user as IUserManagement)?.brandCompany?.displayName &&
                                                    isClientTeam && (
                                                        <div className="d-inline-block">
                                                            <TooltipOverlay content={isTruncated ? brandName : ''}>
                                                                <span className="px-1"> - </span>
                                                                <span className="text-primary">{truncatedName}</span>
                                                            </TooltipOverlay>
                                                        </div>
                                                    )}
                                            </p>
                                            <div className="d-flex align-items-center">
                                                <p className="mb-0 text-primary font-12">
                                                    {!!user?.position && t(`general.positions.${user.position}`)}{' '}
                                                </p>
                                                {isClientTeam && (
                                                    <TooltipOverlay content={isGroupTruncated ? formattedOptions : ''}>
                                                        <p className="mb-0 text-muted">
                                                            {!!user.position && hasGroups && (
                                                                <span className="text-primary px-1"> - </span>
                                                            )}
                                                            {truncatedOptions || ''}
                                                        </p>
                                                    </TooltipOverlay>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <TrashIcon
                                        width="15"
                                        height="15"
                                        onClick={() => {
                                            setIsDeleteModalOpened(true);
                                            setSelectedUser && setSelectedUser(user as unknown as ICompanyUser);
                                        }}
                                        classes="cursor-pointer"
                                    />
                                </div>
                            </CardLayout>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

export default CampaignManagement;
